import React from 'react';
import { Calendar, FileText, CreditCard } from 'lucide-react';
import { Bill } from '../../types/billing';
import { Card } from '../ui/Card';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { Button } from '../ui/Button';

interface BillCardProps {
  bill: Bill;
  onMarkPaid?: (billId: string) => void;
  isProcessing?: boolean;
}

export const BillCard: React.FC<BillCardProps> = ({ bill, onMarkPaid, isProcessing = false }) => {
  const isOverdue = bill.payment_status === 'pending' && new Date(bill.due_date) < new Date();

  return (
    <Card
      title={`Bill ${bill.bill_id}`}
      subtitle={`${bill.vendor_name}`}
      action={<PaymentStatusBadge status={isOverdue ? 'overdue' : bill.payment_status} />}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-slate-950 rounded-lg border border-slate-800">
            <p className="text-[10px] text-slate-500">Bill Date</p>
            <p className="font-semibold text-slate-200">{bill.bill_date}</p>
          </div>
          <div className="p-2 bg-slate-950 rounded-lg border border-slate-800">
            <p className="text-[10px] text-slate-500">Due Date</p>
            <p className={`font-semibold ${isOverdue ? 'text-rose-400' : 'text-slate-200'}`}>{bill.due_date}</p>
          </div>
          <div className="p-2 bg-slate-950 rounded-lg border border-slate-800">
            <p className="text-[10px] text-slate-500">Linked WO</p>
            <p className="font-semibold text-sky-400 font-mono">{bill.work_order_id}</p>
          </div>
          <div className="p-2 bg-slate-950 rounded-lg border border-slate-800">
            <p className="text-[10px] text-slate-500">Linked Quotation</p>
            <p className="font-semibold text-sky-400 font-mono">{bill.quotation_id}</p>
          </div>
        </div>

        <div className="space-y-1">
          {bill.line_items.map((item) => (
            <div key={item.item_id} className="flex justify-between text-xs py-1.5 px-2 bg-slate-950 rounded border border-slate-800">
              <span className="text-slate-300">{item.description} ({item.quantity} {item.unit})</span>
              <span className="text-slate-200 font-medium">Rs {item.amount.toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-700 pt-2 space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Subtotal</span>
            <span className="text-slate-300">Rs {bill.subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Tax</span>
            <span className="text-slate-300">Rs {bill.tax_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-slate-700">
            <span className="text-slate-200">Total</span>
            <span className="text-emerald-400">Rs {bill.total.toLocaleString()}</span>
          </div>
        </div>

        {bill.payment_date && (
          <div className="p-2 bg-emerald-500/5 rounded-lg border border-emerald-500/20 text-xs">
            <p className="text-emerald-400 font-medium">Paid on {bill.payment_date}</p>
            {bill.payment_reference && <p className="text-slate-400 mt-0.5">Ref: {bill.payment_reference}</p>}
          </div>
        )}

        {(bill.payment_status === 'pending' || bill.payment_status === 'overdue') && onMarkPaid && (
          <div className="flex justify-end pt-2">
            <Button variant="primary" size="sm" onClick={() => onMarkPaid(bill.bill_id)} isLoading={isProcessing} icon={<CreditCard className="w-4 h-4" />}>
              Mark as Paid
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
